import React, { useEffect, useMemo, useState } from 'react';
import { pageTitle } from '../../helper';
import { paymentApiUrl, readJsonResponse } from '../../utils/payment';
import Div from '../Div';
import PageHeading from '../PageHeading';
import SectionHeading from '../SectionHeading';
import Spacing from '../Spacing';

export default function CheckoutPage() {
  const [formState, setFormState] = useState({
    customerFullName: '',
    customerEmail: '',
    customerPhone: '',
    serviceDescription: 'Digital Service Payment',
    amount: '',
    currency: 'USD',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [securityInfo, setSecurityInfo] = useState(null);

  useEffect(() => {
    pageTitle('Payment');
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetch(paymentApiUrl('/api/security'))
      .then(readJsonResponse)
      .then(setSecurityInfo)
      .catch(() => {});
  }, []);

  const confirmationRows = useMemo(() => {
    if (!confirmation) return [];
    return [
      ['Merchant', confirmation.merchantName],
      ['Reference Number', confirmation.referenceNumber],
      ['Customer Name', confirmation.customerFullName],
      ['Service', confirmation.serviceDescription],
      ['Amount', Number(confirmation.amount).toFixed(2)],
      ['Currency', confirmation.currency],
      ['Payment Method', confirmation.paymentMethod],
    ];
  }, [confirmation]);

  const handleChange = (field) => (event) => {
    setFormState((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleInitiate = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch(paymentApiUrl('/api/payments/initiate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formState,
          amount: Number(String(formState.amount).replace(',', '.')),
        }),
      });
      const data = await readJsonResponse(response);
      setConfirmation(data.payment);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProceed = async () => {
    if (!confirmation) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(
        paymentApiUrl(`/api/payments/${confirmation.referenceNumber}/checkout`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      const data = await readJsonResponse(response);
      window.location.assign(data.redirectUrl);
    } catch (requestError) {
      setError(requestError.message);
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeading
        title="Payment"
        bgSrc="/images/service_hero_bg.jpeg"
        pageLinkText="Payment"
      />
      <Spacing lg="150" md="80" />
      <Div className="container">
        <Div className="row justify-content-center">
          <Div className="col-lg-7">
            <SectionHeading
              title="Customer Payment <br/>Details"
              subtitle="MPGS Hosted Checkout"
              variant="cs-style1 text-center"
            />
            <Spacing lg="40" md="30" />
            <form onSubmit={handleInitiate} className="row">
              <Div className="col-sm-12">
                <label className="cs-primary_color">Customer Full Name</label>
                <input
                  type="text"
                  className="cs-form_field cs-bg_color_1"
                  value={formState.customerFullName}
                  onChange={handleChange('customerFullName')}
                />
                <Spacing lg="20" md="20" />
              </Div>
              <Div className="col-sm-6">
                <label className="cs-primary_color">Customer Email</label>
                <input
                  type="email"
                  className="cs-form_field cs-bg_color_1"
                  value={formState.customerEmail}
                  onChange={handleChange('customerEmail')}
                />
                <Spacing lg="20" md="20" />
              </Div>
              <Div className="col-sm-6">
                <label className="cs-primary_color">Customer Phone</label>
                <input
                  type="text"
                  className="cs-form_field cs-bg_color_1"
                  value={formState.customerPhone}
                  onChange={handleChange('customerPhone')}
                />
                <Spacing lg="20" md="20" />
              </Div>
              <Div className="col-sm-12">
                <label className="cs-primary_color">Service / Invoice Description</label>
                <input
                  type="text"
                  className="cs-form_field cs-bg_color_1"
                  value={formState.serviceDescription}
                  onChange={handleChange('serviceDescription')}
                />
                <Spacing lg="20" md="20" />
              </Div>
              <Div className="col-sm-6">
                <label className="cs-primary_color">Amount</label>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  className="cs-form_field cs-bg_color_1"
                  value={formState.amount}
                  onChange={handleChange('amount')}
                />
                <Spacing lg="20" md="20" />
              </Div>
              <Div className="col-sm-6">
                <label className="cs-primary_color">Currency</label>
                <select
                  className="cs-form_field cs-bg_color_1"
                  value={formState.currency}
                  onChange={handleChange('currency')}
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="AED">AED</option>
                </select>
                <Spacing lg="20" md="20" />
              </Div>
              <Div className="col-sm-12 text-center" style={{ marginTop: 12 }}>
                <button type="submit" className="cs-btn cs-style1" disabled={loading}>
                  <span>{loading ? 'Generating reference...' : 'Generate Confirmation'}</span>
                </button>
              </Div>
            </form>
            {error ? (
              <>
                <Spacing lg="18" md="14" />
                <p className="cs-m0 text-center" style={{ color: '#ff6b6b' }}>{error}</p>
              </>
            ) : null}
            {confirmation ? (
              <>
                <Spacing lg="50" md="35" />
                <Div
                  className="cs-bg_color_1"
                  style={{ padding: 28, borderRadius: 16, border: '1px solid rgba(255,74,23,0.35)' }}
                >
                  <h3 className="cs-font_30 cs-m0">Payment Confirmation</h3>
                  <Spacing lg="18" md="14" />
                  {confirmationRows.map(([label, value]) => (
                    <Div
                      key={label}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 20,
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                        padding: '10px 0',
                        flexWrap: 'wrap',
                      }}
                    >
                      <strong>{label}</strong>
                      <span>{value}</span>
                    </Div>
                  ))}
                  <Spacing lg="18" md="14" />
                  <p className="cs-m0" style={{ opacity: 0.82 }}>
                    The customer sees the exact amount and currency before opening the hosted payment page.
                  </p>
                  <Spacing lg="24" md="18" />
                  <Div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <button type="button" className="cs-btn cs-style1" onClick={handleProceed} disabled={loading}>
                      <span>{loading ? 'Redirecting...' : 'Proceed to Payment'}</span>
                    </button>
                    <button
                      type="button"
                      className="cs-btn cs-style1"
                      onClick={() => setConfirmation(null)}
                      style={{ backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.2)' }}
                    >
                      <span>Edit Details</span>
                    </button>
                  </Div>
                </Div>
              </>
            ) : null}
            {securityInfo ? (
              <>
                <Spacing lg="50" md="35" />
                <Div
                  className="cs-bg_color_1"
                  style={{ padding: 28, borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <h3 className="cs-font_30 cs-m0">Security Checklist</h3>
                  <Spacing lg="18" md="14" />
                  <p className="cs-m0">HTTPS / SSL: {securityInfo.httpsSsl}</p>
                  <p className="cs-m0">API Security: {securityInfo.apiSecurity}</p>
                  <p className="cs-m0">Data Encryption: {securityInfo.dataEncryption}</p>
                  <p className="cs-m0">3D Secure: {securityInfo.threeDSecure}</p>
                  <p className="cs-m0">Card Data Storage: {securityInfo.cardDataStorage}</p>
                  <p className="cs-m0">VAPT: {securityInfo.vapt}</p>
                  <p className="cs-m0">Incident Response: {securityInfo.incidentResponse}</p>
                </Div>
              </>
            ) : null}
          </Div>
        </Div>
      </Div>
      <Spacing lg="150" md="80" />
    </>
  );
}
